/**
 * CHED-compliant grading constants and helpers for Higher Education.
 * Based on CMO No. 46, Series of 2012 guidelines.
 */

// ---------------------------------------------------------------------------
// Grading Periods
// ---------------------------------------------------------------------------
export const hedPeriods = [
  { value: "prelim",  label: "Preliminary", short: "Prelim",  alt: "PRE", weight: 0.30 },
  { value: "midterm", label: "Midterm",     short: "Midterm", alt: "MID", weight: 0.30 },
  { value: "finals",  label: "Finals",      short: "Finals",  alt: "FIN", weight: 0.40 },
];

/** Default per-class trimester weight map (stored in he_classes.periodWeights). */
export const hedDefaultPeriodWeights = { prelim: 30, midterm: 30, finals: 40 };

// ---------------------------------------------------------------------------
// Grade Components (per period) — school/class configurable
// ---------------------------------------------------------------------------
export const hedDefaultGradeComponents = [
  { type: "ww", name: "Written Works",     percent: 30 },
  { type: "pt", name: "Performance Tasks", percent: 50 },
  { type: "me", name: "Major Exam",        percent: 20 },
];

// ---------------------------------------------------------------------------
// CHED 1.0–5.0 Grade Scale (standard Philippine HEI transmutation)
// ---------------------------------------------------------------------------
export const hedGradeScale = [
  { min: 96, max: 100, points: 1.00, label: "Excellent"    },
  { min: 93, max: 95,  points: 1.25, label: "Superior"     },
  { min: 90, max: 92,  points: 1.50, label: "Very Good"    },
  { min: 87, max: 89,  points: 1.75, label: "Good"         },
  { min: 84, max: 86,  points: 2.00, label: "Good"         },
  { min: 81, max: 83,  points: 2.25, label: "Satisfactory" },
  { min: 78, max: 80,  points: 2.50, label: "Fair"         },
  { min: 75, max: 77,  points: 3.00, label: "Pass"         },
  { min: 0,  max: 74,  points: 5.00, label: "Failed"       },
];

export const HED_PASSING_POINTS = 3.00; // 3.00 and below = passed (lower is better)
export const HED_SPECIAL_GRADES = ["INC", "DRP"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Transmute a percentage grade (0-100) to the CHED 1.0-5.0 scale.
 * @param {number} percentGrade
 * @returns {number} grade points (1.00, 1.25, ... 5.00)
 */
export const transmute = (percentGrade) => {
  if (typeof percentGrade !== "number" || isNaN(percentGrade)) return null;
  const clamped = Math.min(100, Math.max(0, Math.round(percentGrade)));
  const entry = hedGradeScale.find(
    (g) => clamped >= g.min && clamped <= g.max
  );
  return entry ? entry.points : 5.00;
};

/**
 * Look up the label for a grade point value.
 * @param {number|string} points — 1.00 to 5.00, or "INC"/"DRP"
 */
export const hedGradeLabel = (points) => {
  if (HED_SPECIAL_GRADES.includes(points)) return points;
  const entry = hedGradeScale.find((g) => g.points === points);
  return entry ? entry.label : "—";
};

/**
 * Compute the final percentage grade from the three period grades.
 * Default formula: (Prelim × 30%) + (Midterm × 30%) + (Finals × 40%)
 * Accepts an optional weights map { prelim, midterm, finals } (values in %)
 * from he_classes.periodWeights for per-class override.
 * @param {{ prelim: number, midterm: number, finals: number }} periodGrades
 * @param {{ prelim: number, midterm: number, finals: number }} [weights]
 * @returns {number|null}
 */
export const calculateFinalGradePercent = (
  { prelim, midterm, finals },
  weights = hedDefaultPeriodWeights
) => {
  if (
    typeof prelim !== "number" ||
    typeof midterm !== "number" ||
    typeof finals !== "number"
  )
    return null;
  const pw = weights.prelim  / 100;
  const mw = weights.midterm / 100;
  const fw = weights.finals  / 100;
  return Math.round((prelim * pw + midterm * mw + finals * fw) * 100) / 100;
};

/**
 * Compute a period grade from its grade component weighted scores.
 * @param {Array<{ws: number}>} gcTotals — array of { ws } (weighted score out of 100)
 * @returns {number}
 */
export const calculatePeriodGradeFromGCTotals = (gcTotals) => {
  if (!gcTotals || gcTotals.length === 0) return 0;
  const total = gcTotals.reduce((acc, gc) => acc + (gc.ws || 0), 0);
  return Math.round(total * 100) / 100;
};

/**
 * Compute the weighted score (WS) for a grade component.
 * WS = (studentTotal / hps) * 100 * (percent / 100)
 * @param {number} rawTotal — student's total score
 * @param {number} hps — highest possible score of all activities
 * @param {number} percent — GC weight (e.g., 30 for WW)
 * @returns {{ ps: number, ws: number }}
 */
export const computeGCWeightedScore = (rawTotal, hps, percent) => {
  if (!hps || hps === 0) return { ps: 0, ws: 0 };
  const ps = Math.round((rawTotal / hps) * 100 * 100) / 100;
  const ws = Math.round(ps * (percent / 100) * 100) / 100;
  return { ps, ws };
};

/**
 * Compute GWA for a student given all final grade points and subject units.
 * @param {Array<{points: number|string, units: number}>} subjectGrades
 * @returns {number|null}
 */
export const computeGWA = (subjectGrades) => {
  const numeric = subjectGrades.filter(
    (s) => typeof s.points === "number" && !isNaN(s.points) && s.units > 0
  );
  if (numeric.length === 0) return null;
  const totalUnits = numeric.reduce((acc, s) => acc + s.units, 0);
  const weightedSum = numeric.reduce((acc, s) => acc + s.points * s.units, 0);
  return Math.round((weightedSum / totalUnits) * 100) / 100;
};

/**
 * Determine pass/fail remarks from grade points.
 * @param {number|string} points
 * @returns {"passed"|"failed"|string}
 */
export const hedRemarks = (points) => {
  if (HED_SPECIAL_GRADES.includes(points)) return points;
  if (typeof points !== "number") return "—";
  return points <= HED_PASSING_POINTS ? "passed" : "failed";
};

/**
 * Return whether a points value is "passing" (numeric and ≤ 3.00).
 */
export const hedIsPassing = (points) =>
  typeof points === "number" && points <= HED_PASSING_POINTS;

const hedGrades = {
  hedPeriods,
  hedDefaultPeriodWeights,
  hedDefaultGradeComponents,
  hedGradeScale,
  HED_PASSING_POINTS,
  HED_SPECIAL_GRADES,
  transmute,
  hedGradeLabel,
  calculateFinalGradePercent,
  calculatePeriodGradeFromGCTotals,
  computeGCWeightedScore,
  computeGWA,
  hedRemarks,
  hedIsPassing,
};

export default hedGrades;
